import z from 'zod';
import type { Model, ModelSettings } from '../model';
import type { AgentInputItem } from '../types';

export type CompactTrigger = 'auto' | 'manual' | 'reactive' | 'session_memory';
export type CompactSource = 'model_summary' | 'session_memory';
export type HistoryItemRef = string;

export const CompactBoundaryRecordSchema = z.object({
  type: z.literal('compact_boundary'),
  id: z.string(),
  summaryItemId: z.string(),
  compactedRange: z.object({
    fromItemId: z.string(),
    toItemId: z.string(),
  }),
  insertAfterItemId: z.string(),
  supersedesBoundaryId: z.string().optional(),
  trigger: z.enum(['auto', 'manual', 'reactive', 'session_memory']),
  source: z.enum(['model_summary', 'session_memory']),
  createdAt: z.string(),
  status: z.literal('completed'),
  preCompactTokenCount: z.number().optional(),
  postCompactTokenCount: z.number().optional(),
  summarizedItemCount: z.number().optional(),
  preservedItemCount: z.number().optional(),
  summaryTokenCount: z.number().optional(),
  model: z.string().optional(),
});

export const CompactSummaryRecordSchema = z.object({
  type: z.literal('compact_summary'),
  id: z.string(),
  boundaryId: z.string(),
  content: z.string(),
  source: z.enum(['model_summary', 'session_memory']),
  createdAt: z.string(),
});

export const CompactionRecordSchema = z.discriminatedUnion('type', [
  CompactBoundaryRecordSchema,
  CompactSummaryRecordSchema,
]);

export type CompactBoundaryRecord = z.infer<typeof CompactBoundaryRecordSchema>;
export type CompactSummaryRecord = z.infer<typeof CompactSummaryRecordSchema>;
export type CompactionRecord = z.infer<typeof CompactionRecordSchema>;

export interface CompactionStore {
  getRecords(sessionId: string): Promise<CompactionRecord[]>;
  appendRecords(sessionId: string, records: CompactionRecord[]): Promise<void>;
}

export type TranscriptViewItem =
  | AgentInputItem
  | (CompactBoundaryRecord & { virtual?: true })
  | (CompactSummaryRecord & { virtual?: true });

export type SessionMemorySnapshot = {
  content: string;
  lastSummarizedItemId?: string;
  updatedAt?: string;
  tokenCountAtUpdate?: number;
};

export interface SessionMemoryStore {
  read(sessionId: string): Promise<SessionMemorySnapshot | null>;
  write(sessionId: string, snapshot: SessionMemorySnapshot): Promise<void>;
}

export type SessionMemoryConfig =
  | {
      store?: SessionMemoryStore;
      backgroundUpdate?: SessionMemoryBackgroundUpdateConfig;
    }
  | SessionMemoryStore;

export type SessionMemoryBackgroundUpdateConfig =
  | false
  | {
      minimumTokensToInit?: number;
      minimumTokensBetweenUpdate?: number;
      waitForUpdateMs?: number;
      maxMemoryTokens?: number;
      model?: string | Model;
      modelSettings?: ModelSettings;
    };

export type MicrocompactConfig =
  | boolean
  | {
      enabled?: boolean;
      maxToolResultBytes?: number;
      keepRecentToolResults?: number;
      replacementText?: string;
    };

export type LocalCompactionConfig = {
  /**
   * 启用 local compaction
   * @default false
   */
  enabled?: boolean;

  /**
   * 主模型调用前自动判断是否需要压缩， false 则不会自动压缩
   *
   * @default true
   */
  auto?: boolean;

  /**
   * description
   *
   */
  thresholdTokens?: number;

  /**
   * 自动压缩阈值比例， 在没有显式 thresholdTokens 时使用
   * threshold = (contextWindowTokens - reserveOutputTokens) * thresholdRatio
   * e.g. 上下文窗口 128000，预留输出 20000，则阈值约为 (128000 - 20000) * 0.85 = 91800
   *
   * @default 0.85
   */
  thresholdRatio?: number;

  /**
   * 给模型输出预留的token 空间， 不把输入塞满整个 context window
   * 20_000
   * @default 0
   */
  reserveOutputTokens?: number;

  /**
   * description
   *
   */
  contextWindowTokens?: number;

  /**
   * 压缩后保留最近上下文的 token 目标， 注意这是“至少保留这么多token"的最近消息不参与压缩
   *
   * @default 12_000
   */
  preservedTailTokens?: number;

  /**
   * 压缩后至少保留n条最近的非 system 消息
   * @default 8
   */
  preservedTailMessages?: number;

  /**
   * 压缩后至少保留至少 n 条文本消息
   *
   * @default 4
   */
  minTextMessagesToKeep?: number;

  /**
   * 摘要模型最多输出多少token
   * @default 20_000
   */
  maxSummaryTokens?: number;

  model?: string | Model;
  modelSettings?: ModelSettings;
  store?: CompactionStore;
  sessionMemory?: false | SessionMemoryConfig;
  microcompact?: MicrocompactConfig;
};

export type LocalCompactionOption = false | true | LocalCompactionConfig;

export type ManualCompactRunOption =
  | false
  | {
      force?: boolean;
      instructions?: string;
    };

export type ResolvedLocalCompactionConfig = {
  enabled: boolean;
  auto: boolean;
  thresholdRatio: number;
  reserveOutputTokens: number;
  preservedTailTokens: number;
  preservedTailMessages: number;
  minTextMessagesToKeep: number;
  maxSummaryTokens: number;
  failureFuseThreshold: number;
} & Omit<
  LocalCompactionConfig,
  | 'enabled'
  | 'auto'
  | 'thresholdRatio'
  | 'reserveOutputTokens'
  | 'preservedTailTokens'
  | 'preservedTailMessages'
  | 'minTextMessagesToKeep'
  | 'maxSummaryTokens'
>;
